import type { Config } from "../../../../../aysa_api/models/config.types";
import type {
  Session,
  OptionalCustomerSession,
} from "../../../../../aysa_api/models/session.types";
import type { TargetGroup } from "../../../../../aysa_api/models/target_group.types";
import type { ReductionsState } from "../../../../../aysa_api/models/reductions_state.types";
import type { TreeNavigationItem } from "../../../../../aysa_api/models/tree_navigation_item.types";
import type {
  WishlistStreamItem,
  WishlistItemsTab,
  CouponItemsTab,
} from "../../../../../aysa_api/services/wishlist/v1/page/stream.types";
import type {
  WishlistToken,
  WishlistState,
} from "../../../../../aysa_api/models/wishlist.types";
import type { BasketToken } from "../../../../../aysa_api/models/basket_token.types";
import type { DiscountTotalSavings } from "../../../../../aysa_api/models/discount_total_savings.types";
import type { DiscountCardInformationSection } from "../../../../../aysa_api/models/discount_card_information_section.types";
import type { Color } from "../../../../../aysa_api/models/color.types";
import type { Advertisement } from "../../../../../aysa_api/models/advertisement.types";

export enum WishlistSortOptions {
  // Default order (most recently added to wishlist).
  UNSPECIFIED = 0,
  // Sorts the products by their absolute reduction amount
  HIGHEST_REDUCTION = 1,
  // Sorts the products based on their price in an descending order
  HIGHEST_PRICE = 2,
  // Sorts the products based on their price in an ascending order
  LOWEST_PRICE = 3,
  // Sorts the products based on the stock amount
  LOW_STOCK = 4,
}

export interface GetTreeNavigationRequest {
  config: Config;
  session?: Session;
  // The currently selected target group by the client.
  targetGroup: TargetGroup;
  reductionsState: ReductionsState;
}

export interface GetTreeNavigationResponse {
  categoryTree: TreeNavigationItem[];
}

export interface RemoveWishlistItemRequest {
  config: Config;
  session: Session;
  itemKey: string;
}

export interface RemoveWishlistItemResponse {}

export interface GetItemStreamRequest {
  config: Config;
  session: Session;
  limit: number;
  // The currently selected target group by the client.
  targetGroup: TargetGroup;
  lastSeenProductIds: number[];
  basketProductIds: number[];
  reductionsState: ReductionsState;
  sortOptions: WishlistSortOptions;
}

export interface GetItemStreamResponse {
  items: WishlistStreamItem[];
  // Opaque token to retrieve the next page of the stream via `GetItemStreamPage`
  //
  // In case this is `null`, no further pages can be retrieved
  nextState?: Uint8Array;
}

export interface GetItemStreamPageRequest {
  config: Config;
  session: Session;
  streamState: Uint8Array;
  reductionsState: ReductionsState;
}

export interface GetItemStreamPageResponse {
  items: WishlistStreamItem[];
  // Opaque token to retrieve the next page of the stream via `GetItemStreamPage`
  //
  // In case this is `null`, no further pages can be retrieved
  nextState?: Uint8Array;
}

export interface GetWishlistPageRequest {
  config: Config;
  session: Session;
  wishlistToken?: WishlistToken;
  targetGroup: TargetGroup;
  lastSeenProductIds: number[];
  basketProductIds: number[];
  reductionsState: ReductionsState;
  sortOptions: WishlistSortOptions;
  // The first products to be shown in the wishlist
  firstProductIds: number[];
  basketToken?: BasketToken;
  // Signed in customer email
  // If not null newsletter banner may be included
  email?: string;
}

export interface GetWishlistPageResponse {
  // Moved to DiscountCardInformationSection. Use discount_card_information_section instead
  discountTotalSavings?: DiscountTotalSavings;
  // All items of the wishlist
  allItemsTab: WishlistItemsTab;
  // All items of the wishlist that are eligible for the selected discount
  discountedItemsTab?: WishlistItemsTab;
  // / All items of the wishlist that are eligible for any of the coupons
  couponsItemsTab?: CouponItemsTab;
  basketToken?: BasketToken;
  discountCardInformationSection?: DiscountCardInformationSection;
  wishlistState: WishlistState;
  // color bubble for the nav wishlist button
  // use client default when not provided
  bubbleColor?: Color;
}

export interface GetWishlistHeaderBannerRequest {
  config: Config;
  session: OptionalCustomerSession;
  // The currently selected target group by the client.
  targetGroup: TargetGroup;
}

export interface GetWishlistHeaderBannerResponse {
  // BAS Banner
  advertisement?: Advertisement;
}
